import { Link } from "react-router-dom";
import {
  XIcon as X,
  SparkleIcon as Sparkle,
  CheckIcon as Check,
  ShareNetworkIcon as ShareNetwork,
  EnvelopeIcon as Envelope,
  PaperPlaneTiltIcon as PaperPlaneTilt,
} from "@phosphor-icons/react";

export default function PromoteModal({
  workspace,
  deliverableCount,
  readyDeliverableCount,
  audienceNote,
  setAudienceNote,
  promoting,
  result,
  emailCopied,
  onClose,
  onPromote,
  onCopyEmail,
  onOpenEmailClient,
}) {
  const noReady = readyDeliverableCount === 0;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={() => !promoting && onClose()}
      data-testid="promote-modal"
    >
      <div
        className="bg-white border border-[var(--ml-border)] max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkle size={16} weight="bold" color="#0f62fe" />
            <span className="ml-eyebrow">Promote to Client</span>
          </div>
          <button onClick={onClose} className="ml-btn-ghost" disabled={promoting} data-testid="close-promote-modal">
            <X size={16} />
          </button>
        </div>

        {!result ? (
          <PromoteForm
            workspace={workspace}
            deliverableCount={deliverableCount}
            readyDeliverableCount={readyDeliverableCount}
            audienceNote={audienceNote}
            setAudienceNote={setAudienceNote}
            promoting={promoting}
            noReady={noReady}
            onClose={onClose}
            onPromote={onPromote}
          />
        ) : (
          <PromoteResult
            workspace={workspace}
            result={result}
            emailCopied={emailCopied}
            onCopyEmail={onCopyEmail}
            onOpenEmailClient={onOpenEmailClient}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

function PromoteForm({
  workspace,
  deliverableCount,
  readyDeliverableCount,
  audienceNote,
  setAudienceNote,
  promoting,
  noReady,
  onClose,
  onPromote,
}) {
  return (
    <>
      <h2 className="text-xl font-semibold tracking-tight mb-2">{workspace.name}</h2>
      <p className="text-sm text-[var(--ml-text-secondary)] mb-5 leading-relaxed">
        Synthesize this workspace&apos;s {readyDeliverableCount} ready deliverable
        {readyDeliverableCount === 1 ? "" : "s"} into a single polished client
        briefing
        {workspace.client_name ? ` for ${workspace.client_name}` : ""}. You&apos;ll get
        a shareable link and a pre-formatted email body to send.
      </p>

      {noReady ? (
        <div className="border border-[var(--ml-warning)]/40 bg-[var(--ml-warning)]/10 text-sm p-3 mb-5" data-testid="no-ready-banner">
          <strong>No ready deliverables.</strong> Generate at least one deliverable
          in this workspace before promoting.{" "}
          {deliverableCount > 0 &&
            `${deliverableCount} deliverable${
              deliverableCount === 1 ? " is" : "s are"
            } still generating — wait a minute and try again.`}
        </div>
      ) : (
        <div className="mb-5">
          <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1.5">
            Audience note (optional)
          </label>
          <textarea
            value={audienceNote}
            onChange={(e) => setAudienceNote(e.target.value)}
            rows={3}
            className="ml-textarea"
            placeholder="e.g. 'For CIO review ahead of the steering committee on Friday.'"
            data-testid="promote-audience-note"
          />
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="ml-btn-secondary" disabled={promoting}>
          Cancel
        </button>
        <button
          onClick={onPromote}
          className="ml-btn-primary"
          disabled={promoting || noReady}
          data-testid="confirm-promote-btn"
        >
          <Sparkle size={14} weight="bold" />
          {promoting ? "Synthesizing…" : "Generate Briefing"}
        </button>
      </div>
    </>
  );
}

function PromoteResult({
  workspace,
  result,
  emailCopied,
  onCopyEmail,
  onOpenEmailClient,
  onClose,
}) {
  const fullShareUrl = `${window.location.origin}${result.share_url_path}`;
  const isReady = result.status === "ready";
  const isFailed = result.status === "failed";
  return (
    <div data-testid="promote-result">
      <h2 className="text-xl font-semibold tracking-tight mb-2 flex items-center gap-2">
        {isReady ? "Briefing ready" : isFailed ? "Generation failed" : "Synthesizing…"}
      </h2>

      {!isReady && !isFailed && (
        <div className="ml-card p-4 mb-4 flex items-center gap-3 text-sm text-[var(--ml-text-secondary)]">
          <span className="ml-dot" />
          <span className="ml-dot" />
          <span className="ml-dot" />
          <span>
            Claude is reading every deliverable and writing the synthesis. This
            typically takes 30-90 seconds.
          </span>
        </div>
      )}

      {isFailed && (
        <div className="border border-[var(--ml-error)]/30 bg-[var(--ml-error)]/5 p-3 mb-4 text-sm">
          <strong className="text-[var(--ml-error)]">Generation failed.</strong>{" "}
          {result.error || "Please try again."}
        </div>
      )}

      {isReady && (
        <div className="ml-card p-4 mb-4 text-sm">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2">
            New deliverable
          </div>
          <Link
            to={`/deliverables?open=${result.deliverable_id}`}
            className="ml-link font-medium"
            data-testid="open-briefing-link"
          >
            {workspace.name} — Client Briefing →
          </Link>
        </div>
      )}

      <div className="ml-card p-4 mb-4">
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2 flex items-center gap-1.5">
          <ShareNetwork size={11} weight="bold" /> Share link
        </div>
        <div
          className="text-xs ml-mono break-all bg-[var(--ml-surface)] p-2 border border-[var(--ml-border)]"
          data-testid="share-url-display"
        >
          {fullShareUrl}
        </div>
      </div>

      {isReady && (
        <div className="ml-card p-4 mb-4">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2 flex items-center gap-1.5">
            <Envelope size={11} weight="bold" /> Pre-formatted email
          </div>
          <pre className="text-xs whitespace-pre-wrap bg-[var(--ml-surface)] p-3 border border-[var(--ml-border)] mb-2 max-h-48 overflow-y-auto">
            {`Subject: ${result.share_email_subject}

${result.share_email_body.replace(result.share_url_path, fullShareUrl)}`}
          </pre>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={onOpenEmailClient}
              className="ml-btn-primary text-xs"
              data-testid="open-email-client-btn"
              title="Opens your default email client (ProtonMail, Gmail, Outlook, Apple Mail…) with subject and body pre-filled."
            >
              <PaperPlaneTilt size={12} weight="bold" /> Open in email client
            </button>
            <button
              onClick={onCopyEmail}
              className="ml-btn-secondary text-xs"
              data-testid="copy-email-btn"
            >
              {emailCopied ? (
                <>
                  <Check size={12} weight="bold" /> Copied!
                </>
              ) : (
                <>Copy to clipboard</>
              )}
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={onClose} className="ml-btn-primary">
          Done
        </button>
      </div>
    </div>
  );
}
